import { throttle } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { getOperateType } from '@meitu/saas-editor-core/es/framework';
import { IPoint } from '@meitu/saas-editor-core/es/version-types';
import { useStoreActions, useStoreState } from '@/store';
import { AIModifyImagePainType } from '@/store/models/plugin-model';

const getClickPosition = (left: number, top: number, point: IPoint, scale: number) => {
    const result = {
        x: (point.x - left) / scale,
        y: (point.y - top) / scale,
    };

    return result;
};

export const clearCanvas = (canvas: HTMLCanvasElement) => {
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx?.clearRect(0, 0, canvas.width, canvas.height);
};

export const isOutOfImage = (width: number, height: number, point: IPoint) => {
    if (point.x < 0 || point.x > width) {
        return true;
    }

    if (point.y < 0 || point.y > height) {
        return true;
    }

    return false;
};

function calculateControlPoint(P0: IPoint, P1: IPoint, P2: IPoint) {
    const CPx = 2 * P1.x - 0.5 * P0.x - 0.5 * P2.x;
    const CPy = 2 * P1.y - 0.5 * P0.y - 0.5 * P2.y;
    return { x: CPx, y: CPy };
}

const usePain = (scale: number, naturalWidth: number, naturalHeight: number) => {
    const aiModifyImageInfo = useStoreState((state) => state.pluginModel.aiModifyImageInfo);
    const setAIModifyImageInfo = useStoreActions((actions) => actions.pluginModel.setAIModifyImageInfo);
    const { painSize, painType } = aiModifyImageInfo;
    const isAiModifyImage = useStoreState((state) => state.pluginModel.isAiModifyImage);
    const lastPoint = useRef({ x: 0, y: 0 });
    const last2Point = useRef<IPoint | null>({ x: 0, y: 0 });
    const [currentPoint, setPoint] = useState<{ x: number; y: number } | null>(null);

    const isStart = useRef(false);

    useEffect(() => {
        const canvas = document.getElementById('painCanvas') as HTMLCanvasElement;
        const maskCanvas = document.getElementById('painMaskCanvas') as HTMLCanvasElement;
        if (!canvas || !isAiModifyImage || !maskCanvas) {
            setPoint(null);
            return;
        }

        let { left, top } = canvas.getBoundingClientRect();
        setTimeout(() => {
            const rect = canvas.getBoundingClientRect();
            left = rect.left;
            top = rect.top;
        }, 100);

        const resizeWindow = () => {
            const rect = canvas.getBoundingClientRect();
            left = rect.left;
            top = rect.top;
        };
        window.addEventListener('resize', resizeWindow);

        const mousedownHandle = (e: MouseEvent) => {
            const targetType = getOperateType(e.target);
            if (!targetType || targetType !== 'AiModifyImage' || aiModifyImageInfo.resultUrl) {
                setPoint(null);
                return;
            }

            const point = getClickPosition(left, top, { x: e.x, y: e.y }, scale);

            if (!point) {
                setPoint(null);
                return;
            }

            if (isOutOfImage(naturalWidth, naturalHeight, point)) {
                setPoint(null);
            } else {
                setPoint(point);
            }
            setAIModifyImageInfo({
                ...aiModifyImageInfo,
                hasPain: true,
            });
            const ctx = canvas.getContext('2d');
            const maskCtx = maskCanvas.getContext('2d');

            if (!ctx || !maskCtx) {
                return;
            }

            if (painType === AIModifyImagePainType.pain) {
                maskCtx.globalCompositeOperation = 'source-over';
            } else {
                maskCtx.globalCompositeOperation = 'destination-out';
            }

            maskCtx.beginPath();

            maskCtx.fillStyle = '#fff';

            maskCtx.arc(point.x, point.y, painSize / 2 / scale, 0, 2 * Math.PI);
            maskCtx.fill();
            clearCanvas(canvas);
            ctx.globalCompositeOperation = 'source-over';
            ctx.fillStyle = 'rgba(51, 136, 255, 0.6)';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.globalCompositeOperation = 'destination-in';

            ctx.drawImage(maskCanvas, 0, 0);
            last2Point.current = null;
            lastPoint.current = point;
            isStart.current = true;
        };
        canvas.addEventListener('mousedown', mousedownHandle);

        const moveHandle = throttle((e: MouseEvent) => {
            if (aiModifyImageInfo.resultUrl) {
                return;
            }

            const point = getClickPosition(left, top, { x: e.x, y: e.y }, scale);

            if (!isOutOfImage(naturalWidth, naturalHeight, point)) {
                setPoint(point);
            } else {
                setPoint(null);
            }

            if (!isStart.current) {
                return;
            }

            if (!canvas || !isAiModifyImage) {
                setPoint(null);
                return;
            }

            if (!point) {
                setPoint(null);
                return;
            }

            if (isOutOfImage(naturalWidth, naturalHeight, point)) {
                setPoint(null);
            } else {
                setPoint(point);
            }

            const ctx = canvas.getContext('2d');
            const maskCtx = maskCanvas.getContext('2d');

            if (!ctx || !maskCtx) {
                return;
            }

            if (painType === AIModifyImagePainType.pain) {
                maskCtx.globalCompositeOperation = 'source-over';
            } else {
                maskCtx.globalCompositeOperation = 'destination-out';
            }

            maskCtx.beginPath();
            maskCtx.lineWidth = painSize / scale;
            maskCtx.lineJoin = maskCtx.lineCap = 'round';
            maskCtx.strokeStyle = '#fff';

            if (last2Point.current === null) {
                maskCtx.moveTo(lastPoint.current.x, lastPoint.current.y);
                maskCtx.lineTo(point.x, point.y);
                last2Point.current = lastPoint.current;
                lastPoint.current = point;
            } else {
                const lastX = (point.x + lastPoint.current.x) / 2;
                const lastY = (point.y + lastPoint.current.y) / 2;
                maskCtx.moveTo(last2Point.current.x, last2Point.current.y);
                maskCtx.quadraticCurveTo(lastPoint.current.x, lastPoint.current.y, lastX, lastY);
                last2Point.current = { x: lastX, y: lastY };
                lastPoint.current = point;
            }
            // const cpX = (point.x + lastPoint.current.x) / 2;
            // const cpY = (point.y + lastPoint.current.y) / 2;
            // maskCtx.moveTo(lastPoint.current.x, lastPoint.current.y);
            // maskCtx.quadraticCurveTo(cpX, cpY, point.x, point.y);
            // lastPoint.current = { x: point.x, y: point.y };
            maskCtx.stroke();
            clearCanvas(canvas);
            ctx.globalCompositeOperation = 'source-over';
            ctx.fillStyle = 'rgba(51, 136, 255, 0.6)';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.globalCompositeOperation = 'destination-in';

            ctx.drawImage(maskCanvas, 0, 0);
        }, 60);

        document.addEventListener('mousemove', moveHandle);

        const mouseupHandle = () => {
            if (!isStart.current) {
                setPoint(null);
                return;
            }

            isStart.current = false;
            setPoint(null);
        };
        document.addEventListener('mouseup', mouseupHandle);

        return () => {
            document.removeEventListener('mousemove', moveHandle);
            canvas.removeEventListener('mousedown', mousedownHandle);
            document.removeEventListener('mouseup', mouseupHandle);
            window.removeEventListener('resize', resizeWindow);
        };
    }, [
        aiModifyImageInfo,
        isAiModifyImage,
        naturalHeight,
        naturalWidth,
        painSize,
        painType,
        scale,
        setAIModifyImageInfo,
    ]);

    return currentPoint;
};

export default usePain;
